import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plus, Receipt, DollarSign } from "lucide-react";
import { useDespachos } from "@/hooks/useDespachos";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export const ClientReceipts = ({ client }: { client: any }) => {
    const { receipts, addReceipt } = useDespachos();
    const [isOpen, setIsOpen] = useState(false);
    const [receiptNumber, setReceiptNumber] = useState("");
    const [amount, setAmount] = useState("");
    const [concept, setConcept] = useState("");

    if (!client) {
        return (
            <Card>
                <CardContent className="p-8 text-center text-gray-500">Cargando...</CardContent>
            </Card>
        );
    }

    // Filtrar recibos de este cliente
    const clientReceipts = receipts.filter(
        r => r.client_id === client.id || r.client_name?.trim().toLowerCase() === client.name?.trim().toLowerCase()
    );

    const handleCreateReceipt = async () => {
        if (!amount.trim()) return;

        // Generar un número de recibo consecutivo automático si está vacío
        const num = receiptNumber.trim() || `RC-${1000 + receipts.length + 1}`;

        await addReceipt({
            receipt_number: num,
            client_id: client.id,
            client_name: client.name,
            date: new Date().toISOString().split('T')[0],
            amount: parseFloat(amount) || 0,
            concept: concept.trim() || "Pago de Honorarios",
            status: "paid"
        });

        // Limpiar form
        setReceiptNumber("");
        setAmount("");
        setConcept("");
        setIsOpen(false);
    };

    // Formateador
    const formatCurrency = (val: number) => {
        return new Intl.NumberFormat("es-CO", {
            style: "currency",
            currency: "COP",
            minimumFractionDigits: 0
        }).format(val);
    };

    return (
        <Card className="border border-gray-100/80 shadow-sm overflow-hidden bg-white">
            <CardHeader className="flex flex-row items-center justify-between pb-3 bg-gray-50/50 border-b border-gray-100">
                <div>
                    <CardTitle className="text-base font-bold text-gray-900">Recibos de Caja</CardTitle>
                    <p className="text-xs text-gray-400">Historial de abonos, pagos recibidos y facturas emitidas</p>
                </div>
                <Dialog open={isOpen} onOpenChange={setIsOpen}>
                    <DialogTrigger asChild>
                        <Button size="sm" className="rounded-xl bg-legal-blue hover:bg-legal-navy gap-1.5 shadow-md">
                            <Plus className="h-4 w-4" /> Nuevo Recibo
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-2xl">
                        <DialogHeader>
                            <DialogTitle className="text-gray-900 font-bold">Generar Recibo de Caja</DialogTitle>
                            <p className="text-xs text-gray-400">Registra un pago recibido de {client.name}</p>
                        </DialogHeader>
                        <div className="py-4 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Número Recibo</Label>
                                    <Input className="rounded-xl" placeholder="Ej: RC-1001" value={receiptNumber} onChange={e => setReceiptNumber(e.target.value)} />
                                </div>
                                <div className="space-y-2">
                                    <Label>Monto Recibido *</Label>
                                    <Input className="rounded-xl" placeholder="$0.00" value={amount} onChange={e => setAmount(e.target.value)} />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Concepto de Pago *</Label>
                                <Input className="rounded-xl" placeholder="Ej: Abono primera cuota demanda civil" value={concept} onChange={e => setConcept(e.target.value)} />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="ghost" className="rounded-xl" onClick={() => setIsOpen(false)}>Cancelar</Button>
                            <Button className="rounded-xl bg-legal-blue" onClick={handleCreateReceipt} disabled={!amount.trim()}>Generar</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </CardHeader>
            <CardContent className="p-0">
                {clientReceipts.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow className="border-gray-100 hover:bg-transparent">
                                <TableHead className="font-semibold text-gray-500">Recibo No.</TableHead>
                                <TableHead className="font-semibold text-gray-500">Concepto</TableHead>
                                <TableHead className="font-semibold text-gray-500">Fecha</TableHead>
                                <TableHead className="font-semibold text-gray-500">Estado</TableHead>
                                <TableHead className="font-semibold text-gray-500 text-right">Valor</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {clientReceipts.map((r) => (
                                <TableRow key={r.id} className="border-gray-100 hover:bg-blue-50/20 transition-colors">
                                    <TableCell className="font-bold text-gray-800 text-xs">{r.receipt_number || 'RC—000'}</TableCell>
                                    <TableCell className="font-semibold text-gray-700 text-xs">{r.concept}</TableCell>
                                    <TableCell className="text-gray-500 text-xs">{r.date ? new Date(r.date).toLocaleDateString() : '—'}</TableCell>
                                    <TableCell>
                                        <Badge className={`text-[10px] font-bold py-0.5 px-2 rounded-full border ${
                                            r.status === 'paid'
                                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                : r.status === 'pending'
                                                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                                                    : 'bg-red-50 text-red-700 border-red-200'
                                        }`}>
                                            {r.status === 'paid' ? 'Pagado' : r.status === 'pending' ? 'Pendiente' : 'Anulado'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right font-bold text-gray-900 text-xs">
                                        {formatCurrency(r.amount)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <div className="text-center py-16 text-gray-400">
                        <Receipt className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                        <p className="text-sm">No hay recibos de caja emitidos para este cliente.</p>
                        <p className="text-xs text-gray-300 mt-1">Genera un nuevo recibo de caja utilizando el botón superior.</p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
